// Filterleiste — EIN Baustein für Suchfeld plus Auswahllisten über einer Liste.
//
// ⚠️ Seit 2026-08-13 sind es ACHT Verbraucher: Alben, Interpreten und Playlists sind dazugekommen.
// Sie filtern über eine Liste, die vollständig im Speicher liegt (ihre Admin-Endpunkte nehmen
// keinen `search`-Parameter entgegen — Beleg im Kopf von `useSeitenausschnitt`), und sie führen
// deshalb auch keine Entprellung: es geht bei keinem Tastenanschlag eine Anfrage raus. Für diesen
// Baustein ist der Unterschied unsichtbar, er sieht nur Wert und Rückruf.
//
// ⚠️ Beim Zusammenlegen waren es FÜNF, nicht sechs: `WebComponentForm.tsx` hat keine Filterleiste.
// Sein `grid grid-cols-1 md:grid-cols-3 gap-4` unter der Überschrift „Content-Einstellungen" ist
// ein FORMULAR-Raster — eine Sprachauswahl und ein Premium-Haken, kein Suchfeld, kein
// `MagnifyingGlassIcon`, keine Filterlogik. Es teilt mit den fünf echten Leisten nur die
// Raster-Klasse. Wer es hier einsetzt, verdreht ein Formular zu einem Filter.
//
// Gemessener Stand vor dem Zusammenlegen:
//
// | Ort        | Raster            | Felder              | Beschriftung verdrahtet |
// |------------|-------------------|---------------------|-------------------------|
// | Audio      | `md:grid-cols-4`  | Suche + 3 Auswahlen | nein                    |
// | Video      | `md:grid-cols-4`  | Suche + 3 Auswahlen | nein                    |
// | MindBooks  | `md:grid-cols-3`  | Suche + 2 Auswahlen | nein                    |
// | Benutzer   | `md:grid-cols-3`  | Suche + 2 Auswahlen | ja (`useId` + `htmlFor`)|
// | Inhalte    | `md:grid-cols-2`  | Suche + 1 Auswahl   | ja (feste `id`)         |
//
// ⚠️ Die Inhalte-Seite kam als fünfte NACH: sie wurde beim ersten Zusammenlegen übersehen, obwohl
// sie dieselbe Kette trug (`card p-4`, `grid grid-cols-1 md:grid-cols-2 gap-4`, `input pl-10`,
// Lupe, dieselben Beschriftungs-Klassen). Ihre eigene Drift ist damit aufgelöst:
// - Lupe `w-4 h-4` → `w-5 h-5` wie bei den anderen vier.
// - Leere Option „Alle Status" → „Alle". Der Text über der Liste sagt bereits „Status"; die
//   Wiederholung war der Anfang genau der Ausfransung, die dieser Baustein beendet.
// - Feste `id="inhalte-suche"` → `useId`. Eine feste Kennung ist beim zweiten Vorkommen im selben
//   Baum (Vorschau, Test mit zwei Leisten) doppelt und bricht die Verdrahtung still.
//
// Fläche, Polster und Klassenketten sind bei allen fünf identisch
// (`card p-4`, `input pl-10 w-full`, Beschriftung
// `block text-sm font-medium text-secondary-700 mb-2`); die Spaltenzahl ist die einzige echte
// Abweichung — und sie folgt schlicht der Anzahl der Felder. Deshalb rechnet der Baustein sie
// selbst aus, statt sie wählbar zu machen.
//
// **Angleichung nach oben, bewusst:** die Verdrahtung `htmlFor`/`id` über `useId()` kommt aus der
// Benutzer-Verwaltung und gilt jetzt für alle fünf. Das ist kein Etikett-Detail: ohne sie trifft
// ein Klick auf die Beschriftung das Feld nicht, und ein Screenreader sagt nur „Eingabefeld" statt
// „Suche". `useId` statt fester Zeichenketten, weil eine Leiste mehrfach im Baum stehen kann
// (Vorschau, Tests) und doppelte `id`-Werte die Verdrahtung wieder kaputt machen.
//
// **Was beim Angleichen verloren geht:**
// - Audio und Video trugen am Lupen-Symbol zusätzlich die Klasse `transform`. Unter Tailwind 3.4
//   bringt `-translate-y-1/2` die `transform`-Eigenschaft selbst mit; die Klasse ist seit
//   Tailwind 3 wirkungslos und fällt weg. MindBooks und Benutzer fuhren ohnehin schon ohne.
// - Der Text der leeren Auswahl ist fest „Alle". Vier der fünf Kopien trugen exakt dieses Wort,
//   die Inhalte-Seite „Alle Status" — es wählbar zu machen wäre eine Einladung an die nächste
//   Seite, „Beliebig" zu schreiben.
//
// Der Wert-Typ einer Auswahl und die Bindung daran (`FilterAuswahl`, `GebundeneAuswahl`,
// `filterAuswahl`) stehen in `filterZustand.ts`, zusammen mit den geteilten Feld-Definitionen.
// Grund ist keine Geschmacksfrage: `eslint-plugin-react-refresh` verbietet einer Datei, die eine
// Komponente exportiert, zusätzliche Wert-Exporte.
import React, { useId } from 'react';
import { MagnifyingGlassIcon } from '@heroicons/react/24/outline';
import type { GebundeneAuswahl } from './filterZustand';

interface FilterleisteProps {
  /** Inhalt des Suchfelds. */
  suche: string;
  onSuche: (wert: string) => void;
  /** Platzhalter im Suchfeld — der einzige Text, der sich je Seite wirklich unterscheidet. */
  platzhalter: string;
  /** Beliebig viele Auswahllisten, je über `filterAuswahl` gebunden; die Spaltenzahl folgt daraus. */
  auswahlen: readonly GebundeneAuswahl[];
}

/**
 * Spaltenzahl als Nachschlagewerk statt als Zeichenketten-Bau.
 *
 * ⚠️ `md:grid-cols-${n}` wäre der naheliegende Einzeiler und WÄRE KAPUTT: Tailwind liest die
 * Quelldateien als Text und findet nur Klassennamen, die dort vollständig stehen. Eine zur Laufzeit
 * zusammengesetzte Klasse landet nie im erzeugten CSS — das Raster fiele stillschweigend auf eine
 * Spalte zurück, und zwar erst im Produktions-Build, nicht in der Entwicklung.
 */
const SPALTEN_KLASSE: Record<number, string> = {
  1: 'md:grid-cols-1',
  2: 'md:grid-cols-2',
  3: 'md:grid-cols-3',
  4: 'md:grid-cols-4',
  5: 'md:grid-cols-5',
  6: 'md:grid-cols-6',
};

export const Filterleiste: React.FC<FilterleisteProps> = ({ suche, onSuche, platzhalter, auswahlen }) => {
  const feldId = useId();
  const sucheId = `${feldId}-suche`;

  // Eine Spalte für die Suche plus je eine pro Auswahl — genau das Verhältnis, das Audio/Video (4),
  // MindBooks/Benutzer (3) und Inhalte (2) von Hand hingeschrieben hatten. Oberhalb von sechs
  // Spalten wird nicht weiter aufgeteilt: mehr passt auf einem mittelbreiten Schirm ohnehin nicht
  // nebeneinander.
  const spalten = SPALTEN_KLASSE[Math.min(auswahlen.length + 1, 6)];

  return (
    <div className="card p-4">
      <div className={`grid grid-cols-1 ${spalten} gap-4`}>
        <div>
          <label htmlFor={sucheId} className="block text-sm font-medium text-secondary-700 mb-2">
            Suche
          </label>
          <div className="relative">
            <MagnifyingGlassIcon className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-secondary-400" />
            <input
              id={sucheId}
              type="text"
              value={suche}
              onChange={(e) => onSuche(e.target.value)}
              className="input pl-10 w-full"
              placeholder={platzhalter}
            />
          </div>
        </div>
        {auswahlen.map((auswahl, position) => {
          // ⚠️ Kennung und Schlüssel kommen aus der POSITION, nicht mehr aus der Beschriftung.
          // Die Beschriftung ist ein Anzeigetext und darf alles enthalten, was eine Überschrift
          // enthalten darf — „Erstellt am" etwa. Als `id` wäre das laut HTML ungültig (Leerzeichen
          // sind dort verboten): `htmlFor` fände das Feld nicht mehr, `querySelector('#…')` würfe.
          // Und zwei gleich benannte Listen ergäben zwei gleiche React-Schlüssel. Beides ging
          // bisher nur gut, weil alle Beschriftungen zufällig einwortig und eindeutig sind.
          //
          // Die Position als Schlüssel ist hier unbedenklich — und zwar aus einem Grund, nicht aus
          // Bequemlichkeit: die Liste steht je Seite als festes Literal, es wird nichts eingefügt,
          // entfernt oder umsortiert. Selbst wenn doch, hängt an einem kontrollierten `<select>`
          // kein eigener Zustand, den ein Neuaufbau verlöre.
          const auswahlId = `${feldId}-auswahl-${position}`;
          return (
            <div key={auswahlId}>
              <label htmlFor={auswahlId} className="block text-sm font-medium text-secondary-700 mb-2">
                {auswahl.beschriftung}
              </label>
              <select
                id={auswahlId}
                value={auswahl.wert}
                onChange={(e) => auswahl.onChange(e.target.value)}
                className="input w-full"
              >
                <option value="">Alle</option>
                {auswahl.optionen.map((option) => (
                  <option key={option.wert} value={option.wert}>
                    {option.text}
                  </option>
                ))}
              </select>
            </div>
          );
        })}
      </div>
    </div>
  );
};
